import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const transactionsApi = createApi({
  reducerPath: REDUCER_PATHS.TRANSACTIONS,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    //deposits
    depositsList: builder.query({
      query: (data) => ({
        url: `${apiConfig.DEP}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["depositsList"],
    }),
    editDepositsList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.DEP}/${data?.depositId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["depositsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    depositsListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.DEP,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["depositsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    depositsListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.DEP,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["depositsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addDepositsList: builder.mutation({
      query: (data) => ({
        url: apiConfig.DEP,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["depositsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    depositsDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.DEP}/${data?.depositId}`,
        method: "GET",
      }),
      providesTags: ["depositsList"],
    }),
    depositsListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();

        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);

        return {
          url: `${apiConfig.DEP_DOWNLOAD}/${data.type}?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["depositsList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    withdrawalList: builder.query({
      query: (data) => ({
        url: `${apiConfig.WITHDRAWAL}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["withdrawalList"],
    }),
    withdrawalListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.WITHDRAWAL,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["withdrawalList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    withdrawalListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.WITHDRAWAL,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["withdrawalList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addWithdrawalList: builder.mutation({
      query: (data) => ({
        url: apiConfig.WITHDRAWAL,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["withdrawalList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    withdrawalDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.WITHDRAWAL}/${data?.withdrawalId}?slug=${data.slug}&languageId=${data.languageId}`,
        method: "GET",
      }),
      providesTags: ["withdrawalList"],
    }),
    editWithdrawalList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.WITHDRAWAL}/${data?.withdrawalId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["withdrawalList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    withdrawalListDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();

        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);

        return {
          url: `${apiConfig.WITHDRAWAL_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["withdrawalList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
  }),
});

export const {
  useAddDepositsListMutation,
  useLazyDepositsListQuery,
  useDepositsListChangeStatusMutation,
  useDepositsListDeleteMutation,
  useLazyDepositsDetailsQuery,
  useEditDepositsListMutation,
  useWithdrawalListQuery,
  useWithdrawalListChangeStatusMutation,
  useWithdrawalListDeleteMutation,
  useAddWithdrawalListMutation,
  useEditWithdrawalListMutation,
  useWithdrawalDetailsQuery,
  useLazyWithdrawalListQuery,
  useLazyWithdrawalDetailsQuery,
  useDepositsListDownloadMutation,
  useWithdrawalListDownloadMutation,
} = transactionsApi;

export default transactionsApi;
